import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Custom hook for auto-saving form data with visual feedback
 * @param {Object} data - The data to save
 * @param {Function} saveFunction - Function that saves data to database
 * @param {Object} options - Configuration options
 * @returns {Object} Save state and controls
 */
export const useAutoSave = (data, saveFunction, options = {}) => {
  const {
    delay = 500, // Debounce delay in ms
    key = 'auto-save-data', // localStorage key for backup
    enabled = true, // Whether auto-save is enabled
    skipEmpty = true, // Skip saving if data is empty
    onSaveSuccess = null, // Callback on successful save
    onSaveError = null // Callback on save error
  } = options;

  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  
  const timeoutRef = useRef(null);
  const lastDataRef = useRef(null);
  const saveAttemptRef = useRef(0);

  // Save to localStorage as backup
  const saveToLocalStorage = useCallback((dataToSave) => {
    try {
      const backupData = {
        data: dataToSave,
        timestamp: new Date().toISOString(),
        version: saveAttemptRef.current
      };
      localStorage.setItem(key, JSON.stringify(backupData));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }, [key]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          data: parsed.data || parsed, // Handle both new and old formats
          timestamp: parsed.timestamp,
          version: parsed.version || 0
        };
      }
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
    }
    return null;
  }, [key]);

  // Clear localStorage backup
  const clearLocalStorage = useCallback(() => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }, [key]);

  // Perform the actual save
  const performSave = useCallback(async (dataToSave) => {
    if (!enabled || !saveFunction) return;

    // Skip if data is empty and skipEmpty is true
    if (skipEmpty && (!dataToSave || Object.keys(dataToSave).length === 0)) {
      return;
    }

    // Skip if data hasn't changed
    if (JSON.stringify(dataToSave) === JSON.stringify(lastDataRef.current)) {
      return;
    }

    setSaveStatus('saving');
    setError(null);
    saveAttemptRef.current += 1;

    try {
      // Save to localStorage first as backup
      saveToLocalStorage(dataToSave);

      // Attempt database save
      const result = await saveFunction(dataToSave);
      
      // Success
      setSaveStatus('saved');
      setLastSaved(new Date());
      lastDataRef.current = dataToSave;
      
      if (onSaveSuccess) {
        onSaveSuccess(result);
      }

      // Clear the saved status after 2 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);

    } catch (saveError) {
      console.error('Auto-save failed:', saveError);
      setSaveStatus('error');
      setError(saveError);
      
      if (onSaveError) {
        onSaveError(saveError);
      }

      // Auto-retry after 5 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 5000);
    }
  }, [enabled, saveFunction, skipEmpty, saveToLocalStorage, onSaveSuccess, onSaveError]);

  // Debounced save effect
  useEffect(() => {
    if (!enabled || !data) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      performSave(data);
    }, delay);

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, delay, enabled, performSave]);

  // Manual save function
  const saveNow = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    performSave(data);
  }, [data, performSave]);

  // Force save without debounce
  const forceSave = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    await performSave(data);
  }, [data, performSave]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    // Status
    saveStatus,
    lastSaved,
    error,
    
    // Actions
    saveNow,
    forceSave,
    
    // Recovery
    loadFromLocalStorage,
    clearLocalStorage,
    
    // Utils
    hasUnsavedChanges: saveStatus === 'saving' || 
                      (data && JSON.stringify(data) !== JSON.stringify(lastDataRef.current))
  };
};

// Save status indicator component
export const SaveStatusIndicator = ({ 
  saveStatus, 
  lastSaved, 
  error, 
  className = '',
  showText = true 
}) => {
  const getStatusDisplay = () => {
    switch (saveStatus) {
      case 'saving':
        return {
          color: 'text-azure-600',
          icon: '⏳',
          text: 'Saving...'
        };
      case 'saved':
        return {
          color: 'text-emerald-600',
          icon: '✓',
          text: 'Saved'
        };
      case 'error':
        return {
          color: 'text-red-600',
          icon: '⚠',
          text: 'Save failed'
        };
      default:
        return lastSaved ? {
          color: 'text-stone-500',
          icon: '✓',
          text: `Saved ${formatRelativeTime(lastSaved)}`
        } : null;
    }
  };

  const formatRelativeTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  const status = getStatusDisplay();
  if (!status) return null;

  return (
    <div className={`flex items-center gap-1 text-sm ${status.color} ${className}`}>
      <span className="text-xs">{status.icon}</span>
      {showText && <span>{status.text}</span>}
      {error && (
        <span className="text-xs opacity-75" title={error.message}>
          (click to retry)
        </span>
      )}
    </div>
  );
};

// Hook for recovering from localStorage
export const useDataRecovery = (key) => {
  const [recoveryData, setRecoveryData] = useState(null);
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        const data = parsed.data || parsed;
        const timestamp = parsed.timestamp;
        
        // Only show recovery if data is recent (within 24 hours)
        if (timestamp) {
          const dataAge = Date.now() - new Date(timestamp).getTime();
          const isRecent = dataAge < 24 * 60 * 60 * 1000; // 24 hours
          
          if (isRecent && data && Object.keys(data).length > 0) {
            setRecoveryData({ data, timestamp });
            setShowRecoveryPrompt(true);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to check for recovery data:', error);
    }
  }, [key]);

  const acceptRecovery = useCallback(() => {
    setShowRecoveryPrompt(false);
    return recoveryData?.data;
  }, [recoveryData]);

  const declineRecovery = useCallback(() => {
    setShowRecoveryPrompt(false);
    setRecoveryData(null);
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to clear recovery data:', error);
    }
  }, [key]);

  return {
    recoveryData,
    showRecoveryPrompt,
    acceptRecovery,
    declineRecovery
  };
};

export default useAutoSave;